import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { Collection} from '@/newApi/services/ResgateManager';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { StatisticalCode } from '@sysarb/sysarb-types/general/types';
import type { IsDraft, Job, JobProfileEmployment } from '@sysarb/sysarb-types/services/v1/job';
import { queryOptions } from '@tanstack/vue-query';
import { toValue, type MaybeRefOrGetter } from 'vue';

const jobService = new RestManager(`https://${getApiHost()}/job-service`);

type JobProfileOptions = {
    queries: {
        getJobProfiles: (subscribe?: boolean) => ReturnType<typeof queryOptions<Collection<Job> | null, Error, Job[]>>,
        getJobProfile: (jobId: MaybeRefOrGetter<string | null>, subscribe?: boolean) => ReturnType<typeof queryOptions<Job | null, Error, Job>>,
        getJobProfilePositions: (jobId: MaybeRefOrGetter<string | null>, payload: MaybeRefOrGetter<IsDraft>, subscribe?: boolean) => ReturnType<typeof queryOptions<string[] | null, Error, string[]>>,
        getJobProfileStatisticalCodes: (jobId: MaybeRefOrGetter<string | null>, payload: MaybeRefOrGetter<IsDraft>, subscribe?: boolean) => ReturnType<typeof queryOptions<StatisticalCode[] | null, Error, StatisticalCode[]>>,
        getJobProfileEmployments: (jobId: MaybeRefOrGetter<string | null>, payload: MaybeRefOrGetter<IsDraft>) => ReturnType<typeof queryOptions<JobProfileEmployment[] | null, Error, JobProfileEmployment[]>>,
    }
};

export const jobProfiles: JobProfileOptions = {
    queries: {
        getJobProfiles: (subscribe: boolean = true): ReturnType<typeof queryOptions<Collection<Job> | null, Error, Job[]>> => {
            const { instanceTopic } = useLoginStore();
            const resgate = ResgateManager.getInstance();
            const topic = `v1.${instanceTopic}.jobs`;
            return queryOptions<Collection<Job> | null, Error, Job[]>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['jobs', topic],
                queryFn: async (): Promise<Collection<Job> | null> => {
                    return resgate.collection<Job>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                select: (data) => data?.list ?? [],
            });
        },
        getJobProfile: <TData = Job | null>(jobId: MaybeRefOrGetter<string | null>, subscribe: boolean = true): ReturnType<typeof queryOptions<Job | null, Error, TData>> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.job.${toValue(jobId)}`;
            return queryOptions<Job | null, Error, TData>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['job', topic],
                queryFn: async (): Promise<Job | null> => {
                    return ResgateManager.model<Job>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: toValue(jobId) != null,
            });
        },
        getJobProfilePositions: (jobId: MaybeRefOrGetter<string | null>, payload: MaybeRefOrGetter<IsDraft>, subscribe: boolean = true): ReturnType<typeof queryOptions<string[] | null, Error, string[]>> => {
            const { instanceTopic } = useLoginStore();
            const resgate = ResgateManager.getInstance();
            const topic = `v1.${instanceTopic}.job.${toValue(jobId)}`;
            return queryOptions<string[] | null, Error, string[]>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['job', topic, 'positions', toValue(payload)],
                queryFn: async (): Promise<string[] | null> => {
                    return resgate.call<string[]>(topic, 'getpositions', toValue(payload)).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: toValue(jobId) != null,
            });
        },
        getJobProfileStatisticalCodes: (jobId: MaybeRefOrGetter<string | null>, payload: MaybeRefOrGetter<IsDraft>, subscribe: boolean = true): ReturnType<typeof queryOptions<StatisticalCode[] | null, Error, StatisticalCode[]>> => {
            const { instanceTopic } = useLoginStore();
            const resgate = ResgateManager.getInstance();
            const topic = `v1.${instanceTopic}.job.${toValue(jobId)}`;
            return queryOptions<StatisticalCode[] | null, Error, StatisticalCode[]>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['job', topic, 'statisticalcodes', toValue(payload)],
                queryFn: async (): Promise<StatisticalCode[] | null> => {
                    return resgate.call<StatisticalCode[]>(topic, 'getstatisticalcodes', toValue(payload)).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: toValue(jobId) != null,
            });
        },
        getJobProfileEmployments: (jobId: MaybeRefOrGetter<string | null>, payload: MaybeRefOrGetter<IsDraft>): ReturnType<typeof queryOptions<JobProfileEmployment[] | null, Error, JobProfileEmployment[]>> => {
            const { instance } = useLoginStore();
            const url = `v1/${instance}/job/${toValue(jobId)}/getemployments`;
            return queryOptions<JobProfileEmployment[] | null, Error, JobProfileEmployment[]>({
                queryKey: [url, instance, jobId, toValue(payload)],
                queryFn: async (): Promise<JobProfileEmployment[] | null> => {
                    return jobService.post<JobProfileEmployment[]>(toValue(payload), url);
                },
                enabled: toValue(jobId) != null,
            });
        },
    },
};
